import { trackKeepPlaying } from './music-library-jukebox-analytics.js';

(function (window, document) {
    'use strict';
    if (window.iksonJukeboxPayment) return;
    var stripeScript = null;
    var current = null;

    function loadStripe() {
        if (stripeScript) return stripeScript;
        stripeScript = new Promise(function (resolve, reject) {
            var script = document.createElement('script');
            script.src = 'https://js.stripe.com/dahlia/stripe.js';
            script.async = true;
            script.onload = function () { resolve(window.Stripe); };
            script.onerror = function () { script.remove(); stripeScript = null; reject(new Error('Payment options could not load. Please retry.')); };
            document.head.appendChild(script);
        });
        return stripeScript;
    }
    function live(entry) { return current === entry && entry.root.isConnected; }
    function message(entry, text) {
        if (live(entry)) entry.root.querySelector('[data-jukebox-message]').textContent = text;
    }
    function dispose() {
        if (!current) return;
        current.elements.forEach(function (element) { element.destroy(); });
        current.root.querySelector('[data-jukebox-purchase]').hidden = true;
        delete current.root.dataset.jukeboxPurchasing;
        current = null;
    }
    function displayTotal(entry, session) {
        if (!live(entry)) return;
        // Read Stripe's current total as required by Checkout Elements, including
        // after wallet billing details update the tax calculation.
        var total = session.total.total;
        entry.validTotal = session.currency === 'usd' && total.minorUnitsAmount === 100;
        var amount = entry.validTotal ? '$1' : total.amount;
        entry.root.querySelector('[data-jukebox-price]').textContent = '60 minutes of continuous music · ' + amount;
        entry.root.querySelector('[data-jukebox-card-pay]').textContent = 'Keep Playing for ' + amount;
        entry.root.querySelector('[data-jukebox-card-pay]').disabled = entry.busy || !entry.validTotal;
        if (!entry.validTotal) message(entry, 'The payment total changed. Close and reopen Keep Playing to retry.');
    }
    function mountCard(entry) {
        if (!live(entry) || entry.card) return;
        trackKeepPlaying('Card Opened');
        entry.card = entry.checkout.createPaymentElement({
            layout: 'tabs', wallets: { applePay: 'never', googlePay: 'never', link: 'never' }
        });
        entry.elements.push(entry.card);
        entry.card.mount(entry.root.querySelector('[data-jukebox-card-element]'));
        entry.card.on('loaderror', function () {
            if (live(entry)) trackKeepPlaying('Payment Options Failed');
            message(entry, 'The card form could not load. Close and reopen Keep Playing to retry.');
        });
    }
    async function confirm(entry, walletEvent) {
        if (!live(entry) || entry.busy || !entry.validTotal) return;
        entry.busy = true;
        var dialog = entry.root.querySelector('dialog');
        // Stripe can mount a 3DS authentication frame at document level. Temporarily
        // release the native modal top layer so that frame can receive focus.
        var restoreModal = dialog.open;
        if (restoreModal) { dialog.close(); dialog.show(); }
        entry.root.querySelector('[data-jukebox-card-pay]').disabled = true;
        message(entry, 'Confirming payment…');
        var attempted = false;
        try {
            var options = { redirect: 'if_required' };
            if (walletEvent) options.expressCheckoutConfirmEvent = walletEvent;
            else options.email = entry.root.querySelector('[data-jukebox-email]').value.trim();
            trackKeepPlaying('Payment Started', false);
            attempted = true;
            var result = await entry.actions.confirm(options);
            if (result.type === 'error') trackKeepPlaying('Payment Failed', false);
            if (!live(entry)) return;
            if (result.type === 'error') throw new Error(result.error.message);
            message(entry, 'Verifying your listening access…');
            document.dispatchEvent(new CustomEvent('ikson:jukebox-payment-confirmed', {
                detail: { checkoutId: entry.id }
            }));
        } catch (error) {
            if (attempted && !result) trackKeepPlaying('Payment Failed', false);
            message(entry, error.message || 'Payment could not be confirmed. Please try again.');
            if (live(entry)) {
                entry.busy = false;
                entry.root.querySelector('[data-jukebox-card-pay]').disabled = false;
            }
        } finally {
            if (entry.root.isConnected && restoreModal && dialog.open) { dialog.close(); dialog.showModal(); }
        }
    }
    async function open(root, fresh) {
        if (current && current.root === root && !fresh) return;
        dispose();
        var entry = { root: root, elements: [], busy: false, validTotal: false };
        current = entry;
        root.dataset.jukeboxPurchasing = 'true';
        root.querySelector('[data-jukebox-purchase]').hidden = false;
        root.querySelector('[data-jukebox-buy]').hidden = true;
        root.querySelector('[data-jukebox-payment-retry]').hidden = true;
        var fallback = root.querySelector('[data-jukebox-card]');
        fallback.hidden = true;
        fallback.open = false;
        fallback.querySelector('summary').hidden = false;
        message(entry, 'Loading payment options…');
        try {
            var results = await Promise.all([
                loadStripe(),
                fetch(root.dataset.checkoutUrl, { method: 'POST', credentials: 'same-origin', cache: 'no-store',
                    headers: { Accept: 'application/json', 'X-CSRF-TOKEN': root.dataset.csrf }
                }).then(function (response) {
                    if (response.status === 419) throw new Error('Please refresh the page to start a secure payment.');
                    if (!response.ok) throw new Error('Payment options could not load. Please retry.');
                    return response.json();
                })
            ]);
            if (!live(entry)) return;
            var data = results[1];
            entry.id = data.checkout_session_id;
            var stripe = results[0](data.publishable_key);
            entry.checkout = stripe.initCheckoutElementsSdk({
                clientSecret: data.client_secret,
                elementsOptions: { appearance: { theme: 'night', variables: {
                    colorPrimary: '#e8edea', colorBackground: '#141718', colorText: '#f1f1ee', borderRadius: '8px'
                } } },
                adaptivePricing: { allowed: false }
            });
            var loaded = await entry.checkout.loadActions();
            if (!live(entry)) return;
            if (loaded.type !== 'success') throw new Error('Payment options could not load. Please retry.');
            entry.actions = loaded.actions;
            displayTotal(entry, entry.actions.getSession());
            entry.checkout.on('change', function (session) { displayTotal(entry, session); });
            var express = entry.checkout.createExpressCheckoutElement({
                paymentMethodOrder: ['apple_pay', 'google_pay', 'link'],
                paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'auto' },
                buttonType: { applePay: 'buy', googlePay: 'buy' },
                buttonHeight: 48,
                layout: { maxColumns: 1, maxRows: 2, overflow: 'auto' }
            });
            entry.elements.push(express);
            express.on('ready', function (event) {
                if (!live(entry)) return;
                var available = event.availablePaymentMethods || {};
                if (available.applePay) trackKeepPlaying('Apple Pay Available');
                if (available.googlePay) trackKeepPlaying('Google Pay Available');
                if (available.link) trackKeepPlaying('Link Available');
                var hasWallet = Object.keys(available).some(function (key) { return available[key]; });
                fallback.hidden = false;
                fallback.open = !hasWallet;
                fallback.querySelector('summary').hidden = !hasWallet;
                if (!hasWallet) mountCard(entry);
                message(entry, '');
            });
            express.on('loaderror', function () {
                if (!live(entry)) return;
                trackKeepPlaying('Payment Options Failed');
                fallback.hidden = false;
                fallback.open = true;
                mountCard(entry);
                message(entry, 'Wallets are unavailable here. You can pay by card.');
            });
            express.on('confirm', function (event) { confirm(entry, event); });
            express.on('cancel', function () { message(entry, 'Payment cancelled. No purchase was completed.'); });
            express.mount(root.querySelector('[data-jukebox-express]'));
            fallback.ontoggle = function () { if (fallback.open) mountCard(entry); };
        } catch (error) {
            if (live(entry)) trackKeepPlaying('Payment Options Failed');
            message(entry, error.message || 'Payment options could not load. Please retry.');
            if (live(entry)) root.querySelector('[data-jukebox-payment-retry]').hidden = false;
        }
    }
    document.addEventListener('submit', function (event) {
        if (!event.target.matches('[data-jukebox-card-form]')) return;
        event.preventDefault();
        if (current) confirm(current);
    });
    document.addEventListener('click', function (event) {
        if (event.target.closest('[data-jukebox-payment-retry]') && current) open(current.root, true);
    });
    window.iksonJukeboxPayment = { open: open, dispose: dispose };
})(window, document);
